
import { useEffect } from "react";
import { supabase } from "@/lib/supabaseClient";
import { TRUSTED_AUTH_ORIGINS } from "@/config/domains";

/**
 * Page "bridge" appelée en iframe depuis l'autre domaine.
 * Répond via postMessage:
 * - AUTH_SYNC:GET_SESSION -> renvoie access_token/refresh_token si session existe
 * - AUTH_SYNC:LOGOUT -> signe out localement
 */
export default function AuthSyncBridge() {
  useEffect(() => {
    const allowedOrigins = new Set(TRUSTED_AUTH_ORIGINS);

    // Signaler au parent que le bridge est prêt à recevoir des messages
    try {
      if (window.parent && window.parent !== window) {
        window.parent.postMessage({ type: "AUTH_SYNC:BRIDGE_READY" }, "*");
      }
    } catch (_) {}

    async function handler(event) {
      if (!allowedOrigins.has(event.origin)) return;
      const msg = event.data;
      if (!msg || typeof msg !== "object") return;

      const { type, requestId } = msg;
      if (!requestId) return;

      try {
        if (type === "AUTH_SYNC:GET_SESSION") {
          const { data } = await supabase.auth.getSession();
          const s = data?.session;
          const payload = s
            ? { access_token: s.access_token, refresh_token: s.refresh_token }
            : null;

          event.source?.postMessage(
            { type: "AUTH_SYNC:SESSION_RESPONSE", requestId, payload },
            event.origin
          );
        }

        if (type === "AUTH_SYNC:LOGOUT") {
          await supabase.auth.signOut();
          try {
            localStorage.removeItem("artkiv-web-auth");
            sessionStorage.clear();
          } catch (_) {}

          event.source?.postMessage(
            { type: "AUTH_SYNC:SESSION_RESPONSE", requestId, payload: { ok: true } },
            event.origin
          );
        }
      } catch (e) {
        event.source?.postMessage(
          { type: "AUTH_SYNC:ERROR", requestId, payload: { message: String(e?.message || e) } },
          event.origin
        );
      }
    }

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  return null;
}
